if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            // Additionally, search for SKU matches using the full search API
            const searchQuery = this.getQuery();
            if (searchQuery && searchQuery.length > 2) {
              // Remove section_id to get full HTML page instead of section render
              const skuSearchUrl = `${theme.routes.shop_url}/search?q=${encodeURIComponent(searchQuery)}&type=product`;
              
              fetch(skuSearchUrl)
                .then((skuResponse) => skuResponse.text())
                .then((skuResponseText) => {
                  this.mergeSkuResults(skuResponseText);
                  // Set results attribute AFTER SKU search completes to avoid "No Results" flash
                  this.removeAttribute('loading');
                  this.setAttribute('results', '');
                })
                .catch((error) => {
                  console.error('SKU search error:', error);
                  // Even if SKU search fails, show results
                  this.removeAttribute('loading');
                  this.setAttribute('results', '');
                });
            } else {
              // If search query is too short for SKU search, just show predictive results
              this.removeAttribute('loading');
              this.setAttribute('results', '');
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;
      }

      mergeSkuResults(skuResponseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const resultsContainer = document.getElementById(id);
        if (!resultsContainer) return;

        // Parse the full search page HTML
        const parser = new DOMParser();
        const skuDoc = parser.parseFromString(skuResponseText, 'text/html');
        
        // Look for product cards in various possible containers from the search results page
        const skuProductCards = skuDoc.querySelectorAll('.product-card-horizontal, .product-card, .grid--products .grid__item, [data-product-id]');
        
        if (skuProductCards.length === 0) {
          console.log('No SKU product cards found in search results');
          return;
        }

        console.log(`Found ${skuProductCards.length} SKU product matches`);

        // Find or create the products section in predictive search results
        let productsSection = resultsContainer.querySelector('.horizontal-products');
        
        if (!productsSection) {
          // If no products section exists, create one
          const wrapper = document.createElement('div');
          wrapper.className = 'grid gap-3 md:gap-4 w-full';
          wrapper.innerHTML = `
            <p class="search__heading block text-xs uppercase leading-tight tracking-widest">
              Products
            </p>
            <div class="horizontal-products grid"></div>
          `;
          resultsContainer.insertBefore(wrapper, resultsContainer.firstChild);
          productsSection = wrapper.querySelector('.horizontal-products');
        }
        
        // Get existing product IDs to avoid duplicates
        const existingProductIds = new Set();
        resultsContainer.querySelectorAll('[data-product-id]').forEach(el => {
          existingProductIds.add(el.dataset.productId);
        });

        // Append SKU-matched products (limit to first 4 to match predictive search style)
        let addedCount = 0;
        const maxToAdd = 4;
        
        skuProductCards.forEach(productCard => {
          if (addedCount >= maxToAdd) return;
          
          // Try multiple ways to get product identifier
          const productId = productCard.dataset.productId || 
                           productCard.querySelector('[data-product-id]')?.dataset.productId ||
                           productCard.querySelector('a[href*="/products/"]')?.href;
          
          if (productId && !existingProductIds.has(productId)) {
            productsSection.appendChild(productCard.cloneNode(true));
            existingProductIds.add(productId);
            addedCount++;
            console.log(`Added SKU product: ${productId}`);
          }
        });
        
        console.log(`Total SKU products added: ${addedCount}`);
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
